import type { GuestBusinessDetailsPayload } from "@/api/guest-business-details";
import { Checkbox } from "@/components/ui/checkbox";
import FloatingInput from "@/components/ui/floatingInput";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form/form";
import PhoneInputField from "@/components/ui/phone-input/phone-input";
import { removeDialCode } from "@/components/ui/phone-input/phone-input.utils";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import SubmitButton from "@/components/ui/submit-button";
import { COUNTRIES } from "@/constants/countries";
import type { States } from "@/features/states/schema";
import { getTermsOfServiceUrl } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import type { MutationStatus } from "@tanstack/react-query";
import { useRef } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import type { BusinessDetailsFormInitialValues } from "./BusinessDetailsForm.utils";
import { einToPayload, formatEinInput } from "./BusinessDetailsForm.utils";

const businessDetailsFormSchema = z.object({
	business_name: z
		.string()
		.trim()
		.min(1, { message: "Enter your business name" })
		.regex(/^[\p{L}\p{N} ]+$/u, {
			message: "Business name can only contain letters, numbers, and spaces",
		})
		.max(255, { message: "Business name is too long" }),
	address: z
		.string()
		.trim()
		.min(1, { message: "Enter a street address" })
		.max(255, { message: "Address is too long" }),
	address_line_2: z
		.string()
		.max(50, { message: "Use at most 50 characters" })
		.optional(),
	city: z
		.string()
		.trim()
		.min(1, { message: "Enter a city" })
		.max(100, { message: "City name is too long" }),
	zipcode: z
		.string()
		.trim()
		.length(5, { message: "Zip code must be 5 digits" }),
	global_state_id: z
		.number({ message: "Select a state or region" })
		.int()
		.min(1, { message: "Select a state or region" }),
	business_phone: z
		.string({ message: "Enter a business phone number" })
		.min(10, "Please enter a phone number")
		.max(25, "Phone number is too long"),
	business_phone_global_country_id: z
		.number({ message: "Enter a phone number" })
		.int()
		.min(1, "Select a valid country for the phone"),
	ein_number: z.string().refine(
		(value) => {
			const digits = value.replace(/\D/g, "");
			return digits.length === 0 || digits.length === 9;
		},
		{ message: "EIN must be 9 digits" },
	),
	certify: z.boolean().refine((value) => value === true, {
		message: "Please certify and accept the terms to continue",
	}),
});

type BusinessDetailsFormValues = z.infer<typeof businessDetailsFormSchema>;

const USA_COUNTRY_ID = 3;

type BusinessDetailsFormProps = {
	states: States;
	initialValues: BusinessDetailsFormInitialValues;
	submitLabel: string;
	submitStatus: MutationStatus;
	onSubmitForm: (payload: GuestBusinessDetailsPayload) => void;
	title?: string;
};

export function BusinessDetailsForm({
	states,
	initialValues,
	submitLabel,
	submitStatus,
	onSubmitForm,
	title,
}: BusinessDetailsFormProps) {
	const phoneInputRef = useRef<HTMLInputElement>(null);

	const form = useForm<BusinessDetailsFormValues>({
		resolver: zodResolver(businessDetailsFormSchema),
		mode: "onBlur",
		defaultValues: { ...initialValues, certify: false },
	});

	const onSubmit = (values: BusinessDetailsFormValues) => {
		const phoneCountry = Object.values(COUNTRIES).find(
			(c) => c.id === String(values.business_phone_global_country_id),
		);
		if (!phoneCountry) {
			form.setError("business_phone_global_country_id", {
				type: "manual",
				message: "Invalid country selected for business phone.",
			});
			return;
		}
		const localDigits = phoneCountry.secondary
			? removeDialCode(values.business_phone, phoneCountry.secondary)
			: values.business_phone.replace(/\D/g, "");
		const business_phone = localDigits.replace(/\D/g, "").slice(0, 50);
		if (!business_phone) {
			form.setError("business_phone", {
				type: "manual",
				message: "Enter a valid business phone number",
			});
			return;
		}

		onSubmitForm({
			business_name: values.business_name,
			address: values.address,
			address_line_2: values.address_line_2?.trim() || null,
			city: values.city,
			zipcode: values.zipcode,
			global_state_id: values.global_state_id,
			global_country_id: USA_COUNTRY_ID,
			business_phone,
			business_phone_global_country_id: values.business_phone_global_country_id,
			ein_number: einToPayload(values.ein_number),
		});
	};

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
				{title ? (
					<h2 className="text-xl font-bold text-textDark">{title}</h2>
				) : null}

				<FormField
					control={form.control}
					name="business_name"
					render={({ field }) => (
						<FloatingInput
							label="Business name"
							autoComplete="organization"
							type="text"
							value={field.value ?? ""}
							onChange={field.onChange}
							onBlur={field.onBlur}
							error={form.formState.errors.business_name}
						/>
					)}
				/>

				<FormField
					control={form.control}
					name="ein_number"
					render={({ field }) => (
						<FloatingInput
							label="EIN (optional)"
							autoComplete="off"
							inputMode="numeric"
							type="text"
							maxLength={10}
							value={field.value ?? ""}
							onChange={(event) =>
								field.onChange(formatEinInput(event.target.value))
							}
							onBlur={field.onBlur}
							error={form.formState.errors.ein_number}
						/>
					)}
				/>

				<FormField
					control={form.control}
					name="address"
					render={({ field }) => (
						<FloatingInput
							label="Street address"
							autoComplete="street-address"
							type="text"
							value={field.value ?? ""}
							onChange={field.onChange}
							onBlur={field.onBlur}
							error={form.formState.errors.address}
						/>
					)}
				/>

				<FormField
					control={form.control}
					name="address_line_2"
					render={({ field }) => (
						<FloatingInput
							label="Address line 2 (optional)"
							autoComplete="address-line2"
							type="text"
							value={field.value ?? ""}
							onChange={field.onChange}
							onBlur={field.onBlur}
							error={form.formState.errors.address_line_2}
						/>
					)}
				/>

				<FormField
					control={form.control}
					name="city"
					render={({ field }) => (
						<FloatingInput
							label="City"
							autoComplete="address-level2"
							type="text"
							value={field.value ?? ""}
							onChange={field.onChange}
							onBlur={field.onBlur}
							error={form.formState.errors.city}
						/>
					)}
				/>

				<FormField
					control={form.control}
					name="global_state_id"
					render={({ field }) => (
						<FormItem>
							<FormLabel className="text-textDark">State / region</FormLabel>
							<Select
								value={
									field.value != null && field.value > 0
										? String(field.value)
										: undefined
								}
								onValueChange={(v) => field.onChange(Number(v))}
								disabled={!states.states.length}
							>
								<FormControl>
									<SelectTrigger className="border-primary-200 border w-full rounded-default py-5 text-textDark h-15">
										<SelectValue placeholder="Select state" />
									</SelectTrigger>
								</FormControl>
								<SelectContent position="item-aligned">
									{states.states.map((s) => (
										<SelectItem key={s.id} value={String(s.id)}>
											{s.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="zipcode"
					render={({ field }) => (
						<FloatingInput
							label="Zip code"
							autoComplete="postal-code"
							type="text"
							value={field.value ?? ""}
							onChange={field.onChange}
							onBlur={field.onBlur}
							error={form.formState.errors.zipcode}
						/>
					)}
				/>

				<FormField
					control={form.control}
					name="business_phone"
					render={({ field }) => (
						<FormItem className="space-y-0">
							<FormControl>
								<PhoneInputField
									placeholder="Business phone"
									ref={phoneInputRef}
									value={field.value}
									onChange={field.onChange}
									onBlur={field.onBlur}
									onCountryChange={(countryCode) =>
										form.setValue(
											"business_phone_global_country_id",
											countryCode,
										)
									}
									onError={(message) =>
										form.setError("business_phone", {
											type: "manual",
											message,
										})
									}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="business_phone_global_country_id"
					render={({ field }) => (
						<FormControl>
							<input
								type="hidden"
								value={field.value ?? ""}
								onChange={field.onChange}
								onBlur={field.onBlur}
							/>
						</FormControl>
					)}
				/>
				{form.formState.errors.business_phone_global_country_id ? (
					<p className="text-[0.8rem] font-medium text-utility-red">
						{form.formState.errors.business_phone_global_country_id.message}
					</p>
				) : null}

				<FormField
					control={form.control}
					name="certify"
					render={({ field }) => (
						<FormItem className="space-y-0">
							<div className="flex items-center gap-3">
								<FormControl>
									<Checkbox
										className="mt-0.5"
										checked={field.value}
										onCheckedChange={field.onChange}
										onBlur={field.onBlur}
									/>
								</FormControl>
								<FormLabel className="text-sm font-normal leading-snug text-textDark">
									I certify this information is accurate and accept the{" "}
									<a
										href={getTermsOfServiceUrl()}
										target="_blank"
										rel="noreferrer"
										className="font-medium text-primary-600 underline"
									>
										Terms
									</a>{" "}
									&amp; Partner Terms
								</FormLabel>
							</div>
							<FormMessage />
						</FormItem>
					)}
				/>

				<SubmitButton mutationStatus={submitStatus} className="w-full mt-2">
					{submitLabel}
				</SubmitButton>
			</form>
		</Form>
	);
}
